import React, { useState } from 'react'
import { Button } from '@mui/material'
import { ReplySolid, TimesSolid } from '@convious/icons'
import { BaseRouteParams, withNavigation, WithNavigationProps } from 'hocs'
import { ModalDialog } from 'uiComponents/popups/modal'
import { IOrder } from 'orders/schema'
import { getLastPayment } from 'orders/orders/utils'
import SingleCancelRefund from '.'
import { useHasFeature } from 'utils/useHasFeature'

interface RefundActionProps extends WithNavigationProps<BaseRouteParams> {
    order: IOrder
    isCancellation?: boolean
}

const SingleCancelRefundButton: React.FC<RefundActionProps> = ({ order, match, isCancellation = false }) => {
    const [open, setOpen] = useState(false)
    const { accountSlug } = match.params
    const nonRefundableStatuses = ['refunded', 'refunding', 'pending']
    const lastPayment = getLastPayment(order.payments)
    const isCancelledOrder = order.status === 'cancelled'
    // A cancelled order's tickets are already void, but the money is still
    // held while its payment stays "paid" - refunding it is a money-only
    // refund through a dedicated endpoint (BUIL-593).
    const isPostCancellationRefund = !isCancellation && isCancelledOrder
    const showRefundOption =
        lastPayment &&
        (isCancelledOrder
            ? // The orders_list flag is authoritative when the backend sends it;
              // older backends omit it, so fall back to the payment status.
              order.canRefundCancelledOrder ?? lastPayment.paymentStatus === 'paid'
            : !nonRefundableStatuses.includes(lastPayment.paymentStatus)) &&
        Number(order.totalPriceInclTax) > 0 &&
        lastPayment.paymentProvider !== 'Settled externally'
    const showCancelOption =
        order.status !== 'cancelled' && !['refunded', 'refunding'].includes(lastPayment?.paymentStatus ?? '')
    const hasSingleRefundFeature = useHasFeature({ featureName: 'OrderRefunds' })

    if ((!isCancellation && !showRefundOption) || (isCancellation && !showCancelOption) || !hasSingleRefundFeature) {
        return null
    }

    return (
        <>
            {open && (
                <ModalDialog interactive fromTop="6%" className="single-cancel-refund-modal">
                    <SingleCancelRefund
                        accountSlug={accountSlug}
                        orderId={order.id}
                        isCancellation={isCancellation}
                        isPostCancellationRefund={isPostCancellationRefund}
                        onClose={() => setOpen(false)}
                    />
                </ModalDialog>
            )}
            <Button
                onClick={() => setOpen(true)}
                size="medium"
                color="error"
                variant="outlined"
                startIcon={isCancellation ? <TimesSolid /> : <ReplySolid />}
                data-testid={
                    isCancellation
                        ? 'single-cancel-button'
                        : isPostCancellationRefund
                        ? 'single-refund-cancelled-button'
                        : 'single-refund-button'
                }
                className="action-button"
            >
                {isCancellation ? 'Cancel' : 'Refund'}
            </Button>
        </>
    )
}

export default withNavigation(SingleCancelRefundButton)
